/* =============================================================================
   size-calculator.js  —  Archies Size Calculator
   Supports product (PDP drawer) and page (standalone size guide) modes.
============================================================================= */

(() => {
  'use strict';

  let _sharedSizeData = null;

  function getSharedSizeData() {
    if (_sharedSizeData !== null) return _sharedSizeData;
    const el = document.querySelector('script[data-sizecalc-shared-json]');
    if (!el) { _sharedSizeData = []; return _sharedSizeData; }
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      _sharedSizeData = Array.isArray(parsed?.sizes)
        ? parsed.sizes
        : Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('[SizeCalculator] Failed to parse shared size JSON:', e);
      _sharedSizeData = [];
    }
    return _sharedSizeData;
  }

  function interpolate(template, vars) {
    return String(template || '').replace(/[{[]{1}(\w+)[}\]]{1}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
    );
  }

  class SizeCalculator {
    constructor(root, opts = {}) {
      this.$root = root;

      this.sel = Object.assign({
        productJson:          'script[data-sizecalc-variants-json]',
        form:                 '.calculator-form',
        formBtn:              '.calculator-form .button, .calculator-form button[type="submit"]',
        input:                '[data-sizecalc-input]',
        result:               '[data-sizecalc-result]',
        resultForm:           '[data-sizecalc-result-form]',
        sizeText:             '.size-text',
        sizeToast:            '.size-toast',
        cartingVariant:       '[data-sizecalc-carting]',
        cartingForm:          '[data-sizecalc-cart-form]',
        cartingIdInput:       '[data-sizecalc-variant-id]',
        cartingBtn:           '[data-sizecalc-atc-btn]',
        cartingBtnText:       '[data-sizecalc-atc-btn-text]',
        atcLabel:             '[data-sizecalc-atc-label]',
        bisContainer:         '[data-bis-container]',
        altActionsContainer:  '[data-alt-actions]'
      }, opts);

      const rawMode = (this.$root.dataset.sizecalcMode || '').trim().toLowerCase();
      this.mode = rawMode === 'product' ? 'product' : 'page';

      this.unit        = (this.$root.dataset.unit        || 'cm').toLowerCase();
      this.unitLabel   = (this.$root.dataset.unitLabel   || this.unit);
      this.primarySize = (this.$root.dataset.primarySize || 'eu').toLowerCase();

      this.validRange = this.unit === 'inches'
        ? { min: 3,  max: 15 }
        : { min: 20, max: 32 };

      // ── Copy strings — read from JSON blob output by size-calculator.liquid
      // All translatable strings live in <script data-sizecalc-copy> so adding
      // new strings only requires a Liquid + locale file change, not a JS change.
      const copyEl = this.$root.querySelector('script[data-sizecalc-copy]');
      try {
        this.copy = copyEl ? JSON.parse(copyEl.textContent) : {};
      } catch (e) {
        console.error('[SizeCalculator] Failed to parse copy JSON:', e);
        this.copy = {};
      }

      this.sizeData    = getSharedSizeData();
      this.productData = [];

      this.variantIndexByEU       = new Map();
      this.variantIndexBySku      = new Map();
      this.maxVariantEU           = null;
      this.maxAvailableVariantEU  = null;

      this.$productJson         = this.$root.querySelector(this.sel.productJson);
      this.$form                = this.$root.querySelector(this.sel.form);
      this.$formBtn             = this.$root.querySelector(this.sel.formBtn);
      this.$input               = this.$root.querySelector(this.sel.input);
      this.$result              = this.$root.querySelector(this.sel.result);
      this.$resultForm          = this.$root.querySelector(this.sel.resultForm);
      this.$sizeText            = this.$root.querySelector(this.sel.sizeText);
      this.$sizeToast           = this.$root.querySelector(this.sel.sizeToast);
      this.$cartingVariant      = this.$root.querySelector(this.sel.cartingVariant);
      this.$cartingForm         = this.$root.querySelector(this.sel.cartingForm);
      this.$cartingIdInput      = this.$root.querySelector(this.sel.cartingIdInput);
      this.$cartingBtn          = this.$root.querySelector(this.sel.cartingBtn);
      this.$cartingBtnText      = this.$root.querySelector(this.sel.cartingBtnText);
      this.$atcLabel            = this.$root.querySelector(this.sel.atcLabel);
      this.$bisContainer        = this.$root.querySelector(this.sel.bisContainer);
      this.$altActionsContainer = this.$root.querySelector(this.sel.altActionsContainer);

      this.initialFormBtnText = this.$formBtn?.textContent?.trim() || this.copy.submit;

      if (this.mode === 'product') {
        this._parseProductJson();
        this._buildVariantIndexes();
      }

      this._bindEvents();
      this._initUI();
    }

    // =========================================================================
    // Initialisation
    // =========================================================================

    _parseProductJson() {
      if (!this.$productJson) return;
      try {
        const parsed = JSON.parse(this.$productJson.textContent || '[]');
        this.productData = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.variants) ? parsed.variants : [];
      } catch (e) {
        console.error('[SizeCalculator] Failed to parse product variant JSON:', e);
        this.productData = [];
      }
    }

    _bindEvents() {
      if (this.$form) {
        this.$form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.$form.classList.contains('reset-form') ? this.reset() : this.calculate();
        });
      }

      if (this.$cartingForm) {
        this.$cartingForm.addEventListener('submit', (e) => {
          if (this._fitSubmit) {
            this._fitSubmit = false;
            e.preventDefault();
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          const isAriaDisabled = this.$cartingBtn?.getAttribute('aria-disabled') === 'true';
          const variantId = (this.$cartingIdInput?.value || '').trim();
          if (isAriaDisabled || !variantId) return;
          this._addToCartViaApi(variantId);
        });
      }

      document.addEventListener('click', (e) => {
        const colourBtn = e.target?.closest('[data-sizecalc-action="choose-colour"]');
        if (colourBtn) {
          e.preventDefault();
          this._closeDrawer();
          return;
        }

        const fitBtn = e.target?.closest('[data-sizecalc-fit-atc]');
        if (fitBtn) {
          e.preventDefault();
          e.stopPropagation();
          const variantId = (fitBtn.dataset.variantId || '').trim();
          if (!variantId) return;
          this._submitViaThemeAddToCart(variantId);
        }
      });

      if (this.$cartingBtn) {
        this.$cartingBtn.addEventListener('click', (e) => {
          if (this._fitSubmit) return;
          if (this.mode === 'product' && this.$cartingBtn.getAttribute('aria-disabled') === 'true') {
            e.preventDefault();
            this._closeDrawer();
          }
        });
      }
    }

    // =========================================================================
    // Cart API — standard single ATC
    // =========================================================================

    _addToCartViaApi(variantId) {
      if (!variantId) return;

      if (this.$cartingBtn) {
        this.$cartingBtn.setAttribute('aria-disabled', 'true');
        this.$cartingBtn.classList.add('loading');
        this.$cartingBtn.style.pointerEvents = 'auto';
      }
      if (this.$cartingBtnText) {
        this.$cartingBtnText.textContent = '...';
      }

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
        .then((res) => {
          if (!res.ok) return res.json().then((d) => Promise.reject(d));
          return res.json();
        })
        .then(() => { this._onAddToCartSuccess(); })
        .catch((err) => {
          console.error('[SizeCalculator] Cart API error:', err);
          this._onAddToCartError(err);
        });
    }

    // =========================================================================
    // Cart API — fit-card ATC (MUST go through theme handler so drawer updates)
    // =========================================================================

    _submitViaThemeAddToCart(variantId) {
      const parsedId = parseInt(variantId, 10);
      if (!parsedId) return;

      this._fitSubmit = true;

      const form =
        document.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('product-form form') ||
        document.querySelector('form[action*="/cart/add"]');

      if (!form) {
        console.warn('[SizeCalculator] Could not find PDP add-to-cart form.');
        this._fitSubmit = false;
        return;
      }

      const idInput = form.querySelector('[name="id"]');
      if (!idInput) {
        console.warn('[SizeCalculator] Could not find [name="id"] input in PDP form.');
        this._fitSubmit = false;
        return;
      }

      idInput.value = String(parsedId);
      idInput.dispatchEvent(new Event('input', { bubbles: true }));
      idInput.dispatchEvent(new Event('change', { bubbles: true }));

      const qtyInput = form.querySelector('[name="quantity"]');
      if (qtyInput) {
        qtyInput.value = '1';
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const themeBtn =
        form.querySelector('[data-btn-addtocart]') ||
        document.querySelector('[data-btn-addtocart]') ||
        form.querySelector('button[name="add"], button[type="submit"]') ||
        document.getElementById('product-add-to-cart');

      if (!themeBtn) {
        console.warn('[SizeCalculator] Could not find theme add-to-cart button.');
        this._fitSubmit = false;
        return;
      }

      themeBtn.click();

      window.setTimeout(() => {
        document.body.classList.add('cart-sidebar-show');
        this._fitSubmit = false;
      }, 200);
    }

    _onAddToCartSuccess() {
      if (this.$cartingBtn) {
        this.$cartingBtn.setAttribute('aria-disabled', 'false');
        this.$cartingBtn.classList.remove('loading');
        this.$cartingBtn.style.pointerEvents = '';
        this.$cartingBtn.style.cursor = '';
      }
      if (this.$cartingBtnText) {
        this.$cartingBtnText.textContent = this.copy.addToCart;
      }
    }

    _onAddToCartError(err) {
      if (this.$cartingBtn) {
        this.$cartingBtn.setAttribute('aria-disabled', 'false');
        this.$cartingBtn.classList.remove('loading');
        this.$cartingBtn.style.pointerEvents = '';
        this.$cartingBtn.style.cursor = '';
      }
      if (this.$cartingBtnText) {
        this.$cartingBtnText.textContent = this.copy.addToCart;
      }
      // Uses translated copy string — falls back to English if key missing
      const msg = err?.description || err?.message || this.copy.cartError || 'Could not add to cart. Please try again.';
      this._showToast('<p>' + msg + '</p>', 'orange');
    }

    _initUI() {
      this._setCartState('hidden');
      this._hideAtcLabel();
      this._hideBIS();
      this._clearAltActions();
      this._clearToasts();
    }

    // =========================================================================
    // Variant indexing (product mode)
    // =========================================================================

    _parseVariantEU(name) {
      const str = String(name || '').trim().replace(/,/g, '.');
      const m   = str.match(/EU\s*([\d]+(?:\.[\d]+)?)\s*[-\u2013]\s*([\d]+(?:\.[\d]+)?)/i);
      return m ? { min: parseFloat(m[1]), max: parseFloat(m[2]) } : null;
    }

    _euKey(min, max) { return `${Number(min)}-${Number(max)}`; }

    _buildVariantIndexes() {
      this.variantIndexByEU.clear();
      this.variantIndexBySku.clear();
      this.maxVariantEU          = null;
      this.maxAvailableVariantEU = null;

      for (const v of this.productData) {
        if (v?.sku) this.variantIndexBySku.set(String(v.sku).trim(), v);
        const r = this._parseVariantEU(v?.name);
        if (!r) continue;
        this.variantIndexByEU.set(this._euKey(r.min, r.max), v);
        if (!this.maxVariantEU ||
            r.max > this.maxVariantEU.max ||
            (r.max === this.maxVariantEU.max && r.min > this.maxVariantEU.min)) {
          this.maxVariantEU = { ...r, name: v.name, variant: v };
        }
        if (v?.available === true) {
          if (!this.maxAvailableVariantEU ||
              r.max > this.maxAvailableVariantEU.max ||
              (r.max === this.maxAvailableVariantEU.max && r.min > this.maxAvailableVariantEU.min)) {
            this.maxAvailableVariantEU = { ...r, name: v.name, variant: v };
          }
        }
      }
    }

    _findExactVariantForEU(eu) {
      const min = eu?.min_length, max = eu?.max_length;
      if (!isFinite(min) || !isFinite(max)) return null;
      return this.variantIndexByEU.get(this._euKey(min, max)) || null;
    }

    // =========================================================================
    // Between-sizes detection
    // =========================================================================

    _isBetweenSizes(match) {
      return match?.additional_info?.enable_note === true;
    }

    _findRelaxedFitMatch(match) {
      const idx = this.sizeData.indexOf(match);
      if (idx === -1) return null;
      const currentKey = this._euKey(match.eu?.min_length, match.eu?.max_length);
      for (let i = idx + 1; i < this.sizeData.length; i++) {
        const candidate    = this.sizeData[i];
        const candidateKey = this._euKey(candidate.eu?.min_length, candidate.eu?.max_length);
        if (candidateKey !== currentKey) return candidate;
      }
      return null;
    }

    // =========================================================================
    // Size matching
    // =========================================================================

    _findMatchByCm(cm) {
      if (!Array.isArray(this.sizeData) || !this.sizeData.length) return null;
      return this.sizeData.find(
        (s) => cm >= s?.cm?.min_length && cm <= s?.cm?.max_length
      ) || null;
    }

    // =========================================================================
    // Size label localisation
    // =========================================================================

    _buildSizeLabel(match) {
      const eu     = match.eu  || {};
      const euMin  = eu.min_length ?? '';
      const euMax  = eu.max_length ?? euMin;
      const euStr  = `EU ${euMin}\u2013${euMax}`;

      if (this.primarySize === 'eu' || !match[this.primarySize]) {
        return { primary: euStr, secondary: null };
      }

      const local    = match[this.primarySize];
      const localMin = local.min_length ?? '';
      const localMax = local.max_length ?? localMin;
      const system   = this.primarySize.toUpperCase();

      return { primary: `${system} ${localMin}\u2013${localMax}`, secondary: euStr };
    }

    // =========================================================================
    // DOM helpers
    // =========================================================================

    _setHTML(el, html) { if (el) el.innerHTML = html; }
    _show(el)          { if (el) el.style.display = 'block'; }
    _hide(el)          { if (el) el.style.display = 'none'; }

    _setInputValid()   { this.$input?.setAttribute('aria-invalid', 'false'); }
    _setInputInvalid() { this.$input?.setAttribute('aria-invalid', 'true'); }

    _focusResult() {
      if (this.$result && typeof this.$result.focus === 'function') {
        setTimeout(() => this.$result.focus({ preventScroll: false }), 50);
      }
    }

    _focusInput() {
      if (this.$input && typeof this.$input.focus === 'function') {
        setTimeout(() => this.$input.focus({ preventScroll: true }), 50);
      }
    }

    // ── Toasts ───────────────────────────────────────────────────────────────

    _clearExtraToasts() {
      this.$sizeToast?.parentNode
        ?.querySelectorAll('.size-toast--extra')
        .forEach((el) => el.remove());
    }

    _clearToasts() {
      if (!this.$sizeToast) return;
      this.$sizeToast.innerHTML = '';
      this.$sizeToast.classList.remove('display-note');
      [...this.$sizeToast.classList]
        .filter((c) => c.endsWith('-color'))
        .forEach((c) => this.$sizeToast.classList.remove(c));
      this._hide(this.$sizeToast);
      this._clearExtraToasts();
    }

    _showToast(html, color = null) {
      if (!this.$sizeToast) return;
      this.$sizeToast.innerHTML = html;
      this.$sizeToast.classList.add('display-note');
      if (color) this.$sizeToast.classList.add(`${color}-color`);
      this._show(this.$sizeToast);
    }

    _renderExtraToasts(items) {
      if (!Array.isArray(items) || !items.length) return;
      const parent = this.$sizeToast?.parentNode;
      if (!parent) return;
      items.forEach((item) => {
        const el = document.createElement('div');
        el.className = 'size-toast size-toast--extra display-note';
        if (item.note_color) {
          el.classList.add(`${item.note_color}-color`);
          el.style.setProperty('--note-color', item.note_color);
        }
        if (item.type) el.dataset.type = item.type;
        el.innerHTML = item.html || '';
        el.style.display = 'block';
        parent.insertBefore(el, this.$sizeToast.nextSibling);
      });
    }

    // ── ATC label ────────────────────────────────────────────────────────────

    _hideAtcLabel() {
      if (!this.$atcLabel) return;
      this.$atcLabel.innerHTML = '';
      this._hide(this.$atcLabel);
    }

    _setAtcLabel(variantName) {
      if (!this.$atcLabel) return;
      const colour = (this.$root.dataset.productColour || '').trim() || 'Selected colour';
      const title  = (this.$root.dataset.productTitle  || '').trim() || 'Product';

      const titleEl = document.createElement('div');
      titleEl.className = 'calculator-atc-label__title';
      const strong = document.createElement('strong');
      strong.textContent = colour;
      titleEl.appendChild(strong);
      titleEl.appendChild(document.createTextNode(' \u2013 ' + title));

      const optEl = document.createElement('div');
      optEl.className = 'calculator-atc-label__option';
      optEl.textContent = String(variantName || '').trim();

      this.$atcLabel.innerHTML = '';
      this.$atcLabel.appendChild(titleEl);
      this.$atcLabel.appendChild(optEl);
      this._show(this.$atcLabel);
    }

    // ── Alt actions ──────────────────────────────────────────────────────────

    _clearAltActions() {
      if (this.$altActionsContainer) {
        this.$altActionsContainer.innerHTML = '';
        this._hide(this.$altActionsContainer);
      }
      if (this._$fitContainer) {
        this._$fitContainer.remove();
        this._$fitContainer = null;
      }
    }

    _renderAltActionsForOOS() {
      if (this.mode !== 'product') return;
      if (!this.$altActionsContainer) return;

      this.$altActionsContainer.innerHTML = `
        <div class="sizecalc-alt-actions">
          <button type="button" class="button button--secondary" data-sizecalc-action="choose-colour">
            ${this.copy.oosChooseColour}
          </button>
        </div>
      `;
      this._show(this.$altActionsContainer);
    }

    // ── Between-sizes dual ATC ────────────────────────────────────────────────

    _renderFitContainer() {
      if (this._$fitContainer && this._$fitContainer.isConnected) {
        return this._$fitContainer;
      }
      const el = document.createElement('div');
      el.className = 'sizecalc-fit-container';
      const anchor = this.$cartingVariant || this.$cartingForm || this.$sizeToast || this.$result;
      if (anchor) {
        anchor.insertAdjacentElement('afterend', el);
      } else {
        this.$root.appendChild(el);
      }
      this._$fitContainer = el;
      return el;
    }

    _renderSingleRecommendCard(match, variant) {
      this._setCartState('hidden');
      this._hideAtcLabel();
      const container = this._renderFitContainer();

      const currencySymbol = { eu: '\u20ac', uk: '\u00a3' }[this.primarySize] || '$';
      const formatPrice = (v) => {
        if (!v || typeof v.price === 'undefined') return '';
        const amount = (parseInt(v.price, 10) / 100).toFixed(2).replace(/\.00$/, '');
        return `${currencySymbol}${amount}`;
      };
      const stripSystem = (label) =>
        label.replace(/^[A-Z]{1,3}\s+/i, '').replace(/\s*[\u2013\-]\s*/g, ' - ');

      const { primary } = this._buildSizeLabel(match);
      const sizeRange   = stripSystem(primary);
      const price       = variant?.available === true ? formatPrice(variant) : '';
      const colour      = (this.$root.dataset.productColour || '').trim();
      const colourStr   = colour ? `<strong>${colour}</strong> - ` : '';
      const atcText     = this.copy.addToCart || 'Add to Cart';

      const isBoundary = match?.additional_info?.enable_note === true;
      // Uses translated copy strings
      const heading    = isBoundary
        ? (this.copy.weRecommend    || 'We recommend:')
        : (this.copy.yourPerfectSize || 'Your perfect size:');

      if (variant?.available === true) {
        const btnLabel = price ? `${atcText} - ${price}` : atcText;
        container.innerHTML = `
          <div class="sizecalc-fit-choice">
            <div class="sizecalc-fit-card sizecalc-fit-card--available sizecalc-fit-card--single">
              <p class="sizecalc-fit-card__heading">${heading}</p>
              <p class="sizecalc-fit-card__detail">${colourStr}${sizeRange}</p>
              <button
                type="button"
                class="button button--primary sizecalc-fit-card__atc"
                data-sizecalc-fit-atc
                data-variant-id="${variant.id}"
                data-original-label="${btnLabel}"
                aria-label="${btnLabel}"
              ><span data-fit-btn-text>${btnLabel}</span></button>
            </div>
          </div>
        `;
      } else if (variant && variant.available === false) {
        container.innerHTML = `
          <div class="sizecalc-fit-choice">
            <div class="sizecalc-fit-card sizecalc-fit-card--oos sizecalc-fit-card--single">
              <p class="sizecalc-fit-card__heading">${heading}</p>
              <p class="sizecalc-fit-card__detail">${colourStr}${sizeRange}</p>
              <p class="sizecalc-fit-card__oos-msg">${this.copy.oosTitle || 'Out of stock'}</p>
              <button
                type="button"
                class="button button--secondary"
                data-bis-button
                data-bis-variant-id="${variant.id}"
                data-bis-variant-sku="${variant.sku || ''}"
                data-bis-style-sku="${(this.$root.dataset.styleSku || '').trim()}"
                data-bis-product-handle="${(this.$root.dataset.productHandle || '').trim()}"
                data-bis-product-url="${(this.$root.dataset.productUrl || '').trim()}"
              >${this.copy.oosNotify || 'Notify me'}</button>
            </div>
          </div>
        `;
      }
      container.style.display = 'block';
    }

    _renderBetweenSizesATC(snugMatch, snugVariant, relaxedMatch) {
      this._setCartState('hidden');
      this._hideAtcLabel();
      const container = this._renderFitContainer();

      const relaxedVariant = relaxedMatch ? this._findExactVariantForEU(relaxedMatch.eu) : null;
      const snugLabel      = this._buildSizeLabel(snugMatch);
      const relaxedLabel   = relaxedMatch ? this._buildSizeLabel(relaxedMatch) : null;
      const colour         = (this.$root.dataset.productColour || '').trim();
      const title          = (this.$root.dataset.productTitle  || '').trim();
      const atcText        = this.copy.addToCart || 'Add to Cart';

      const bisData = {
        productHandle: (this.$root.dataset.productHandle || '').trim(),
        productUrl:    (this.$root.dataset.productUrl    || '').trim(),
        styleSku:      (this.$root.dataset.styleSku      || '').trim()
      };

      const snugCard = this._buildFitCard({
        fitType: 'snug',
        sizeLabel: snugLabel.primary,
        sizeSecondary: snugLabel.secondary,
        variant: snugVariant,
        colour, title, atcText, bisData
      });

      const relaxedCard = relaxedLabel ? this._buildFitCard({
        fitType: 'relaxed',
        sizeLabel: relaxedLabel.primary,
        sizeSecondary: relaxedLabel.secondary,
        variant: relaxedVariant,
        colour, title, atcText, bisData
      }) : '';

      container.innerHTML = `
        <div class="sizecalc-fit-choice">
          ${snugCard}
          ${relaxedCard}
        </div>
      `;
      container.style.display = 'block';

      try {
        const kInit =
          window.klaviyoBIS?.init ||
          window.klaviyo?.initBackInStock ||
          window.__klaviyoBackInStockInit ||
          null;
        if (typeof kInit === 'function') kInit();
      } catch (e) {
        console.warn('[SizeCalculator] Klaviyo BIS init failed:', e);
      }
    }

    _buildFitCard({ fitType = 'snug', sizeLabel, sizeSecondary, variant, colour, title, atcText, bisData }) {
      const currencySymbol = { eu: '\u20ac', uk: '\u00a3' }[this.primarySize] || '$';

      const formatPrice = (v) => {
        if (!v || typeof v.price === 'undefined') return '';
        const amount = (parseInt(v.price, 10) / 100).toFixed(2).replace(/\.00$/, '');
        return `${currencySymbol}${amount}`;
      };

      const stripSystem = (label) => label.replace(/^[A-Z]{1,3}\s+/i, '').replace(/\s*[\u2013\-]\s*/g, ' - ');
      const sizeRange = stripSystem(sizeLabel);

      const colourStr  = colour ? `<strong>${colour}</strong>` : '';
      const detailLine = colourStr
        ? `<p class="sizecalc-fit-card__detail">${colourStr} - ${sizeRange}</p>`
        : `<p class="sizecalc-fit-card__detail">${sizeRange}</p>`;

      // Uses translated copy strings for fit card headings
      const fitHeading = fitType === 'snug'
        ? (this.copy.snugFitHeading    || 'For a snug fit we recommend:')
        : (this.copy.relaxedFitHeading || 'For a relaxed fit we recommend:');

      // ── In stock ──────────────────────────────────────────────────────────
      if (variant?.available === true) {
        const price    = formatPrice(variant);
        const btnLabel = price ? `${atcText} - ${price}` : atcText;
        return `
          <div class="sizecalc-fit-card sizecalc-fit-card--available sizecalc-fit-card--${fitType}">
            <p class="sizecalc-fit-card__heading">${fitHeading}</p>
            ${detailLine}
            <button
              type="button"
              class="button button--primary sizecalc-fit-card__atc"
              data-sizecalc-fit-atc
              data-variant-id="${variant.id}"
              data-original-label="${btnLabel}"
              aria-label="${btnLabel}"
            ><span data-fit-btn-text>${btnLabel}</span></button>
          </div>
        `;
      }

      // ── Out of stock ──────────────────────────────────────────────────────
      if (variant && variant.available === false) {
        return `
          <div class="sizecalc-fit-card sizecalc-fit-card--oos sizecalc-fit-card--${fitType}">
            <p class="sizecalc-fit-card__heading">${fitHeading}</p>
            ${detailLine}
            <p class="sizecalc-fit-card__oos-msg">${this.copy.oosTitle || 'Out of stock'}</p>
            <button
              type="button"
              class="button button--secondary"
              data-bis-button
              data-bis-variant-id="${variant.id}"
              data-bis-variant-sku="${variant.sku || ''}"
              data-bis-style-sku="${bisData.styleSku}"
              data-bis-product-handle="${bisData.productHandle}"
              data-bis-product-url="${bisData.productUrl}"
            >${this.copy.oosNotify || 'Notify me'}</button>
          </div>
        `;
      }

      // ── Size not in this colour ────────────────────────────────────────────
      return `
        <div class="sizecalc-fit-card sizecalc-fit-card--unavailable sizecalc-fit-card--${fitType}">
          <p class="sizecalc-fit-card__heading">${fitHeading}</p>
          ${detailLine}
          <button
            type="button"
            class="button button--secondary sizecalc-fit-card__colour-btn"
            data-sizecalc-action="choose-colour"
            style="pointer-events:auto;cursor:pointer;"
          >${this.copy.unavailableColourBtn || 'Select another colour'}</button>
        </div>
      `;
    }

    // ── Cart state machine ────────────────────────────────────────────────────

    _setCartState(state, { variantId = null, btnText = null } = {}) {
      const show    = state !== 'hidden';
      const enabled = state === 'enabled';

      if (this.$cartingVariant) {
        this.$cartingVariant.style.display = show ? 'block' : 'none';
        this.$cartingVariant.style.opacity = (enabled || this.mode === 'product') ? '1' : '0.55';
      }

      if (this.$cartingIdInput) {
        this.$cartingIdInput.value = enabled && variantId ? String(variantId) : '';
      }

      if (this.$cartingBtn) {
        this.$cartingBtn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        if (this.mode === 'product' && !enabled) {
          this.$cartingBtn.style.pointerEvents = 'auto';
          this.$cartingBtn.style.cursor        = 'pointer';
        } else {
          this.$cartingBtn.style.pointerEvents = '';
          this.$cartingBtn.style.cursor        = '';
        }
      }

      if (this.$cartingBtnText && btnText !== null) {
        this.$cartingBtnText.textContent = btnText;
      }

      if (!show) this._hideAtcLabel();
    }

    // ── BIS ──────────────────────────────────────────────────────────────────

    _hideBIS() {
      if (!this.$bisContainer) return;
      this.$bisContainer.innerHTML = '';
      this._hide(this.$bisContainer);
    }

    _renderBIS({ variantId, variantSku } = {}) {
      if (!this.$bisContainer) return;

      const productHandle = (this.$root.dataset.productHandle || '').trim();
      const productUrl    = (this.$root.dataset.productUrl    || '').trim();
      const styleSku      = (this.$root.dataset.styleSku      || '').trim();

      this.$bisContainer.innerHTML = `
        <button
          type="button"
          class="button button--primary"
          data-bis-button
          data-bis-variant-id="${variantId  ? String(variantId)  : ''}"
          data-bis-variant-sku="${variantSku ? String(variantSku) : ''}"
          data-bis-style-sku="${styleSku}"
          data-bis-product-handle="${productHandle}"
          data-bis-product-url="${productUrl}"
        >
          ${this.copy.oosNotify}
        </button>
      `;
      this._show(this.$bisContainer);

      try {
        const kInit =
          window.klaviyoBIS?.init ||
          window.klaviyo?.initBackInStock ||
          window.__klaviyoBackInStockInit ||
          null;
        if (typeof kInit === 'function') kInit();
      } catch (e) {
        console.warn('[SizeCalculator] Klaviyo BIS init failed:', e);
      }
    }

    // ── Close drawer / popup ──────────────────────────────────────────────────

    _closeDrawer() {
      const modalRoot = this.$root.closest('[data-sizecalc-modal-root]');

      if (modalRoot) {
        const closeBtn =
          modalRoot.querySelector('[data-popup-close], .popup-close, .js-popup-close') ||
          modalRoot.querySelector('button[aria-label*="close" i]')                     ||
          modalRoot.querySelector('button.close, .modal__close, .drawer__close')       ||
          null;
        if (closeBtn) { closeBtn.click(); return; }
        document.dispatchEvent(new CustomEvent('popup:close', {
          bubbles: true,
          detail:  { id: modalRoot.id || null }
        }));
        return;
      }

      const drawerRoot =
        this.$root.closest('[data-drawer], .drawer, [id$="-drawer"], [id$="Drawer"]');

      if (drawerRoot) {
        const closeBtn =
          drawerRoot.querySelector('[data-drawer-close], .drawer__close, .js-drawer-close') ||
          drawerRoot.querySelector('button[aria-label*="close" i]')                         ||
          null;
        if (closeBtn) { closeBtn.click(); return; }
      }

      ['drawer:close', 'theme:drawer:close', 'sidedrawer:close'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { drawer: drawerRoot } }));
      });

      const root = modalRoot || drawerRoot;
      if (root) {
        ['is-open', 'active', 'open', 'drawer--open', 'popup--open'].forEach((cls) =>
          root.classList.remove(cls)
        );
      }
    }

    // =========================================================================
    // Core: calculate()
    // =========================================================================

    calculate() {
      if (!this.$input || !this.$result || !this.$sizeText) return;

      this.$result.innerHTML = '';
      this._clearToasts();
      this._hideBIS();
      this._clearAltActions();
      if (this.mode === 'product') {
        this._setCartState('hidden');
        this._hideAtcLabel();
      }

      const raw        = String(this.$input.value || '').trim().replace(',', '.');
      const enteredVal = parseFloat(raw);

      if (Number.isNaN(enteredVal)) {
        this._setInputInvalid();
        this.$sizeText.innerHTML = this.copy.invalidNumber;
        return;
      }

      const footLengthCm = this.unit === 'inches'
        ? parseFloat((enteredVal * 2.54).toFixed(2))
        : enteredVal;

      if (enteredVal < this.validRange.min || enteredVal > this.validRange.max) {
        this._setInputInvalid();
        const msg = interpolate(this.copy.outOfRange, {
          min:  this.validRange.min,
          max:  this.validRange.max,
          unit: this.unitLabel
        });
        this.$sizeText.innerHTML = msg;
        return;
      }

      const match = this._findMatchByCm(footLengthCm);

      if (!match) {
        this._setInputInvalid();
        this.$form?.classList.add('reset-form');
        if (this.$formBtn) this.$formBtn.textContent = this.copy.reset;
        this.$sizeText.innerHTML = this.copy.noMatchTitle;
        this.$result.innerHTML   = `<p>${this.copy.noMatchBody}</p>`;
        return;
      }

      this._setInputValid();

      // Analytics — URL hash for session tracking
      history.replaceState(null, '', '#size-calculator');

      // Analytics — cart attribute so Shopify Flow can tag the order
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { 'size_calculator_used': 'true' }
        })
      }).catch(() => {});

      this.$form?.classList.add('reset-form');
      if (this.$formBtn) this.$formBtn.textContent = this.copy.reset;

      const resultIntroHtml = interpolate(this.copy.resultIntro, {
        value: enteredVal,
        unit:  this.unitLabel
      });
      this.$sizeText.innerHTML = resultIntroHtml;

      const { primary, secondary } = this._buildSizeLabel(match);
      const isBetween = this._isBetweenSizes(match);

      const secondaryHtml = secondary
        ? `<p class="size-result-card__secondary">${secondary}</p>`
        : '';
      const resultHtml = `
        <div class="size-result-card${isBetween ? ' size-result-card--between' : ''}">
          <p class="size-result-card__primary"><strong>${primary}</strong></p>
          ${secondaryHtml}
        </div>
      `;

      this.$result.innerHTML = resultHtml;
      this._focusResult();

      // ── PAGE mode ─────────────────────────────────────────────────────────
      if (this.mode !== 'product') {
        if (match.additional_info?.enable_note === true) {
          this._showToast(match.additional_info.note || '', match.additional_info.note_color || null);
        }
        this._renderExtraToasts(match.extra_info);
        return;
      }

      // ── PRODUCT mode — variant matching ───────────────────────────────────

      const wantedVariant = this._findExactVariantForEU(match.eu);

      // Case A: Size not offered in this colour at all
      if (!wantedVariant) {
        const bestLabel = this.maxAvailableVariantEU?.name || this.maxVariantEU?.name || null;
        const maxLabel  = bestLabel ? String(bestLabel).replace(/^EU\s*/i, '') : null;
        const euMin     = match.eu?.min_length ?? '';
        const euMax     = match.eu?.max_length ?? euMin;

        const msg = maxLabel
          ? `<p><strong>Sorry!</strong> This colour isn't available in <strong>EU ${euMin}\u2013${euMax}</strong>. The largest size available in this colour is <strong>${maxLabel}</strong>.</p>`
          : `<p><strong>Sorry!</strong> This size isn't available in this colour.</p>`;

        this._showToast(msg, 'orange');
        this._setCartState('disabled', { btnText: this.copy.unavailableColourBtn });
        return;
      }

      // Case B: Between sizes — show both snug + relaxed options
      if (isBetween) {
        if (match.additional_info?.enable_note === true) {
          this._showToast(
            match.additional_info.note || '',
            match.additional_info.note_color || null
          );
        }
        const relaxedMatch = this._findRelaxedFitMatch(match);
        if (relaxedMatch) {
          this._renderBetweenSizesATC(match, wantedVariant, relaxedMatch);
        } else {
          this._renderSingleRecommendCard(match, wantedVariant);
        }
        this._renderExtraToasts(match.extra_info);
        return;
      }

      // Case C: Single size, variant exists but OOS
      if (wantedVariant.available !== true) {
        this._showToast(
          `<p><strong>${this.copy.oosTitle}</strong><br>${this.copy.oosBody}</p>`,
          'orange'
        );
        this._setCartState('disabled', { btnText: this.copy.oosBtn });
        this._setAtcLabel(wantedVariant.name);
        this._renderBIS({ variantId: wantedVariant.id, variantSku: wantedVariant.sku });
        this._renderAltActionsForOOS();
        return;
      }

      // Case D: Single size, in stock
      this._renderSingleRecommendCard(match, wantedVariant);
    }

    // =========================================================================
    // Core: reset()
    // =========================================================================

    reset() {
      // Clear analytics hash on reset
      history.replaceState(null, '', window.location.pathname + window.location.search);

      this.$form?.classList.remove('reset-form');
      if (this.$formBtn) this.$formBtn.textContent = this.initialFormBtnText;

      this.$result.innerHTML   = '';
      if (this.$resultForm) this.$resultForm.innerHTML = '';
      this.$sizeText.innerHTML = this.copy.promptEnter;

      this._setInputValid();
      this._clearToasts();
      this._hideBIS();
      this._clearAltActions();

      if (this.mode === 'product') {
        this._setCartState('hidden');
        this._hideAtcLabel();
      }

      this._focusInput();
    }
  }

  // ===========================================================================
  // Boot
  // ===========================================================================

  function injectStyles() {
    if (document.getElementById('sizecalc-fit-choice-styles')) return;
    const style = document.createElement('style');
    style.id = 'sizecalc-fit-choice-styles';
    style.textContent = `
      .sizecalc-fit-choice {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 4px;
        box-sizing: border-box;
      }
      .sizecalc-fit-container {
        width: 100%;
        box-sizing: border-box;
        padding-top: 20px;
      }
      .sizecalc-fit-card {
        border-radius: 14px;
        padding: 16px;
        background: #ffffff;
        box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 4px 14px rgba(0,0,0,0.07);
        display: flex;
        flex-direction: column;
        gap: 6px;
        text-align: center;
      }
      .sizecalc-fit-card--unavailable {
        background: #f8f9fa;
        box-shadow: none;
        border: 1.5px dashed #d1d5db;
        opacity: 0.7;
      }
      .sizecalc-fit-card__heading {
        font-size: 14px;
        color: #4a5568;
        margin: 0;
        line-height: 1.3;
      }
      .sizecalc-fit-card__heading em {
        font-style: normal;
        font-weight: 700;
        color: #1a3c4d;
      }
      .sizecalc-fit-card__detail {
        font-size: 15px;
        font-weight: 400;
        color: #1a3c4d;
        margin: 0 0 4px;
        line-height: 1.3;
      }
      .sizecalc-fit-card__detail strong {
        font-weight: 700;
      }
      .sizecalc-fit-card__atc {
        width: 100% !important;
        margin: 8px auto 0 !important;
        display: block !important;
        box-sizing: border-box !important;
        text-align: center !important;
      }
      .sizecalc-fit-card__oos-msg {
        font-size: 12px;
        color: #94a3b8;
        margin: 0 0 4px;
        font-weight: 500;
      }
    `;
    document.head.appendChild(style);
  }

  function boot(root = document) {
    injectStyles();
    getSharedSizeData();
    const wrappers  = root.querySelectorAll('[data-size-calculator], .size-calculator-wrapper');
    const instances = [];
    wrappers.forEach((el) => {
      if (el.dataset.sizeCalcInit === '1') return;
      el.dataset.sizeCalcInit = '1';
      instances.push(new SizeCalculator(el));
    });
    window._archiesSizeCalculators = (window._archiesSizeCalculators || []).concat(instances);
    window._archiesSizeCalculator  = window._archiesSizeCalculators[0] || null;
    return instances;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (e) => {
    _sharedSizeData = null;
    if (e?.target) boot(e.target);
  });

})();